import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Configuration d'axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

// Intercepteur pour ajouter le token et la langue à chaque requête
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const lang = localStorage.getItem('i18nextLng') || 'fr';
    config.headers['Accept-Language'] = lang;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur de réponse : déconnexion automatique si token expiré
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      // Ne pas traiter les erreurs d'auth sur les endpoints de login/register
      const url = error.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/forgot-password');
      if (!isAuthEndpoint) {
        const token = localStorage.getItem('token');
        if (token) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          // Notifier AuthContext via un événement custom
          window.dispatchEvent(new Event('auth:token-expired'));
          // Éviter toute boucle de redirection si on est déjà sur /login
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  // Inscription en 2 étapes
  startRegistration: (email) => api.post('/auth/register/start', { email }),
  verifyRegistrationToken: (token) => api.get(`/auth/register/verify/${token}`),
  completeRegistration: (data) => api.post('/auth/register/complete', data),

  // Connexion
  login: (data) => api.post('/auth/login', data),
  getProfile: () => api.get('/auth/profile'),

  // Réinitialisation mot de passe
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyResetToken: (token) => api.get(`/auth/reset-password/${token}`),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password })
};

// Products API (Marketplace Généraliste)
export const productsAPI = {
  // Produits
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  getUserProducts: (page = 1, limit = 24, status = '', search = '') => api.get('/products/my-products', { params: { page, limit, status, search } }),
  getBySeller: (sellerId) => api.get(`/products/seller/${sellerId}`),
  getFavorites: () => api.get('/products/favorites'),
  create: (formData) => api.post('/products', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/products/${id}`, data),
  publish: (id) => api.put(`/products/${id}/publish`),
  unpublish: (id) => api.put(`/products/${id}/unpublish`),
  markAsSold: (id) => api.put(`/products/${id}/sold`),
  delete: (id) => api.delete(`/products/${id}`),
  addToFavorites: (id) => api.post(`/products/${id}/favorite`),
  removeFromFavorites: (id) => api.delete(`/products/${id}/favorite`),
  shareToFacebook: (id) => api.post(`/products/${id}/share-facebook`),

  // Stats publiques
  getPublicStats: () => api.get('/products/stats/public'),

  // Images
  getImages: (id) => api.get(`/products/${id}/images`),
  setPrimaryImage: (id, imagePath) => api.put(`/products/${id}/primary-image`, { image_path: imagePath }),

  // Import en masse (admin + vendeurs pro)
  bulkTemplate: () => api.get('/products/bulk/template', { responseType: 'blob' }),
  bulkImport: (formData) => api.post('/products/bulk/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Catégories
  getCategories: () => api.get('/products/categories'),
  getMainCategories: () => api.get('/products/categories/main'),
  getCategoriesHierarchy: () => api.get('/products/categories/hierarchy'),
  getCategoryCounts: () => api.get('/products/categories/counts'),
  getCategoryBySlug: (slug) => api.get(`/products/categories/slug/${slug}`),
  getSubcategories: (parentId) => api.get(`/products/categories/${parentId}/subcategories`)
};

// Legacy Events API (pour compatibilité)
export const annoncesAPI = productsAPI;
export const eventsAPI = productsAPI;

// Payment API
export const paymentAPI = {
  createPaymentIntent: (eventId, paymentMethod = 'card') => api.post('/payment/create-payment-intent', {
    eventId,
    paymentMethod
  }),
  confirmPayment: (paymentIntentId) => api.post('/payment/confirm-payment', { paymentIntentId }),
  getHistory: () => api.get('/payment/history')
};

// Contact API
export const contactAPI = {
  send: (data) => api.post('/contact', data),
  getMessages: () => api.get('/contact/messages'),
  reply: (id, reply) => api.post(`/contact/messages/${id}/reply`, { reply })
};

// Conversations API (messagerie vendeur/acheteur)
export const conversationsAPI = {
  start: (productId, message) => api.post('/conversations/start', { productId, message }),
  startSupport: (subject, message) => api.post('/conversations/start-support', { subject, message }),
  getAll: () => api.get('/conversations'),
  getMessages: (conversationId) => api.get(`/conversations/${conversationId}`),
  sendMessage: (conversationId, message) => api.post(`/conversations/${conversationId}/message`, { message }),
  getUnreadCount: () => api.get('/conversations/unread-count')
};

// Marketplace API (système Vinted-like)
export const marketplaceAPI = {
  // Onboarding vendeur (Stripe Connect)
  getSellerCountries: () => api.get('/marketplace/seller/countries'),
  createSellerAccount: (country) => api.post('/marketplace/seller/account', { country }),
  getOnboardingLink: () => api.post('/marketplace/seller/onboarding-link'),
  getSellerStatus: () => api.get('/marketplace/seller/status'),
  getStripeDashboardLink: () => api.post('/marketplace/seller/dashboard-link'),

  // Achat
  calculateOrderTotal: (productId, shippingMethod) => api.post('/marketplace/order/calculate', {
    productId,
    shippingMethod
  }),
  createPurchaseIntent: (productId, shippingMethod, shippingAddress, buyerMessage, paymentMethod = 'card') => api.post('/marketplace/order/create', {
    productId,
    shippingMethod,
    shippingAddress,
    buyerMessage,
    paymentMethod
  }),
  confirmPurchase: (paymentIntentId) => api.post('/marketplace/order/confirm', { paymentIntentId }),

  // Panier
  calculateCartTotal: (items) => api.post('/marketplace/cart/calculate', { items }),
  createCartPurchaseIntent: (items, shippingAddress, paymentMethod = 'card') => api.post('/marketplace/cart/create', { items, shippingAddress, paymentMethod }),

  // Expédition
  getCarriers: () => api.get('/marketplace/carriers'),
  markAsShipped: (orderId, data = {}) => api.put(`/marketplace/order/${orderId}/ship`, data),

  // Livraison
  confirmDelivery: (orderId) => api.post(`/marketplace/order/${orderId}/confirm-delivery`),

  // Litiges
  openDispute: (orderId, reason) => api.post(`/marketplace/order/${orderId}/dispute`, { reason }),

  // Porte-monnaie
  getWalletBalance: () => api.get('/marketplace/wallet/balance'),
  getSalesHistory: () => api.get('/marketplace/wallet/sales'),
  getPurchaseHistory: () => api.get('/marketplace/orders/purchases'),

  // Masquer
  hideOrder: (orderId) => api.put(`/marketplace/order/${orderId}/hide`),

  // Compteurs
  getPendingMovementsCount: () => api.get('/marketplace/pending-count'),
  markMovementsSeen: () => api.post('/marketplace/mouvements-seen')
};

// Notifications API (notifications admin)
export const notificationsAPI = {
  getMy: () => api.get('/notifications/my'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.post(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/read-all')
};

// Reviews API (avis vendeur)
export const reviewsAPI = {
  // Créer un avis
  create: (orderId, rating, comment) => api.post('/reviews', { orderId, rating, comment }),

  // Avis d'un vendeur
  getSellerReviews: (sellerId, page = 1, limit = 10) =>
    api.get(`/reviews/seller/${sellerId}`, { params: { page, limit } }),

  // Stats d'un vendeur
  getSellerStats: (sellerId) => api.get(`/reviews/seller/${sellerId}/stats`),

  // Vérifier si on peut laisser un avis
  canReview: (orderId) => api.get(`/reviews/can-review/${orderId}`),

  // Récupérer l'avis d'une commande
  getOrderReview: (orderId) => api.get(`/reviews/order/${orderId}`)
};

// Admin Conversations API (surveillance)
export const adminConversationsAPI = {
  getStats: () => api.get('/admin/conversations/stats'),
  getAll: (params) => api.get('/admin/conversations', { params }),
  getMessages: (id) => api.get(`/admin/conversations/${id}`)
};

// Google Reviews API
export const googleReviewsAPI = {
  get: () => api.get('/google-reviews')
};

export default api;
